/**
 * Menu — minimal popover button.
 *
 * Renders a trigger button; clicking it opens a panel of items
 * positioned below+right. Closes on item-click, click-outside, Esc,
 * or focus-out. Used in the toolbar for the Export menu and
 * elsewhere where a multi-action button is more compact than a row
 * of three buttons.
 *
 * Intentionally tiny (~50 lines) — no portal, no animation, no
 * arrow-key nav. If we need a richer menu later, swap to Radix UI
 * or a similar headless lib; until then this matches the codebase's
 * "do less" aesthetic.
 */
import {
  ReactNode, useEffect, useRef, useState, type CSSProperties,
} from 'react';

interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Right-aligned hint (e.g. keyboard shortcut). */
  shortcut?: string;
  /** Optional left-aligned icon. */
  icon?: ReactNode;
  /** Optional secondary text under the label. */
  description?: string;
  /** When true, render a divider above this item. */
  divider?: boolean;
  danger?: boolean;
}

interface Props {
  /** What the trigger button looks like. */
  trigger: ReactNode;
  items: MenuItem[];
  /** Forward to the trigger button. Use to apply your button class
   *  (eg `btn-primary`). */
  triggerClassName?: string;
  triggerTitle?: string;
  triggerStyle?: CSSProperties;
  /** Optional pre-text label in the panel header (eg "Export as"). */
  headerLabel?: string;
  /** Anchor side. Default 'right' aligns the menu's right edge to
   *  the trigger's right edge. */
  align?: 'left' | 'right';
  disabled?: boolean;
}

export function Menu({
  trigger,
  items,
  triggerClassName,
  triggerTitle,
  triggerStyle,
  headerLabel,
  align = 'right',
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape. Bound only when open to avoid
  // a global listener whenever a closed menu exists.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="btn-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={triggerClassName}
        title={triggerTitle}
        style={triggerStyle}
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && (
        <div
          className="btn-menu-pop"
          role="menu"
          style={align === 'left' ? { left: 0, right: 'auto' } : undefined}
        >
          {headerLabel && (
            <div
              style={{
                padding: '4px 10px 6px',
                fontSize: 'var(--fs-xs)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-faint)',
              }}
            >
              {headerLabel}
            </div>
          )}
          {items.map((it, i) => (
            <div key={i}>
              {it.divider && (
                <div
                  style={{
                    height: 1,
                    background: 'var(--border)',
                    margin: '4px 6px',
                  }}
                />
              )}
              <button
                type="button"
                role="menuitem"
                className="btn-menu-item"
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
                style={
                  it.danger
                    ? { color: 'var(--danger-700)' }
                    : undefined
                }
              >
                {it.icon && (
                  <span style={{ marginRight: 8, display: 'inline-flex' }}>
                    {it.icon}
                  </span>
                )}
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block' }}>{it.label}</span>
                  {it.description && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-faint)',
                        fontWeight: 400,
                        marginTop: 1,
                      }}
                    >
                      {it.description}
                    </span>
                  )}
                </span>
                {it.shortcut && (
                  <span className="kbd" style={{ marginLeft: 8 }}>
                    {it.shortcut}
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
